import { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
// utils
import {
    TSkill,
    TSkillData,
    TSkillParameter,
    TSkillParameterData
} from '@newo/api/gateway';
import {
    useLibrarySkillsQuery,
    useCreateLibrarySkillMutation,
    useDeleteLibrarySkillMutation,
    useUpdateLibrarySkillMutation,
    useCreateLibrarySkillParameterMutation,
    useDeleteLibrarySkillParameterMutation,
    useUpdateLibrarySkillParameterMutation
} from '@/services/designer/library_skills/library_skills.queries';
import { openConfirmDeleteModal } from '@/utils/ui';
import useLibraryStore from '@/pages/Library/Library.store';

const EMPTY_SKILLS: TSkill[] = [];
export const useSkills = (libraryId?: string) => {
    const setSelectedSkillId = useLibraryStore(
        (state) => state.setSelectedSkillId
    );
    const selectedSkillId = useLibraryStore((state) => state.selectedSkillId);
    if (!libraryId) {
        throw new Error('Library ID is required');
    }
    const {
        data: skills = EMPTY_SKILLS,
        refetch,
        isLoading: isSkillsLoading
    } = useLibrarySkillsQuery(libraryId);
    const { mutateAsync: createSkillAsync } = useCreateLibrarySkillMutation({
        onSuccess: async ({ id }) => {
            await refetch();
            setSelectedSkillId(id);
        }
    });
    const { mutate: deleteSkill } = useDeleteLibrarySkillMutation({
        onSuccess: () => {
            refetch();
        }
    });
    const { mutateAsync: updateSkillAsync } = useUpdateLibrarySkillMutation({
        onSuccess: () => {
            refetch();
        }
    });

    const handleCreateSkill = useCallback(
        async (data: TSkillData) => {
            return createSkillAsync({ libraryId, data });
        },
        [createSkillAsync, libraryId]
    );

    const handleUpdateSkill = useCallback(
        async (skillId: string, data: TSkillData) => {
            return updateSkillAsync({ libraryId, skillId, data });
        },
        [updateSkillAsync, libraryId]
    );

    const handleDeleteSkill = useCallback(
        (skill: TSkill) => {
            openConfirmDeleteModal({
                title: `Delete ${skill.idn} skill?`,
                children: `Are you sure you want to delete ${skill.idn} skill?`,
                confirmLabel: 'Delete Skill',
                onConfirm: () => {
                    deleteSkill(
                        { libraryId, skillId: skill.id },
                        {
                            onSuccess: () => {
                                if (selectedSkillId === skill.id) {
                                    setSelectedSkillId(undefined);
                                }
                                refetch();
                            }
                        }
                    );
                }
            });
        },
        [deleteSkill, libraryId, refetch]
    );

    return {
        skills,
        refetch,
        isSkillsLoading,
        onSkillCreate: handleCreateSkill,
        onSkillDelete: handleDeleteSkill,
        onSkillUpdate: handleUpdateSkill
    };
};

export const useSkillParameters = (libraryId?: string, skillId?: string) => {
    const queryClient = useQueryClient();
    if (!libraryId) {
        throw new Error('Library ID and skill ID are required');
    }

    const refetchSkills = useCallback(() => {
        queryClient.invalidateQueries({
            queryKey: ['library/skills', libraryId]
        });
    }, [queryClient, libraryId]);

    const { mutateAsync: createSkillParameterAsync } =
        useCreateLibrarySkillParameterMutation({
            onSuccess: () => {
                refetchSkills();
            }
        });

    const { mutateAsync: deleteSkillParameterAsync } =
        useDeleteLibrarySkillParameterMutation({
            onSuccess: () => {
                refetchSkills();
            }
        });

    const { mutateAsync: updateSkillParameterAsync } =
        useUpdateLibrarySkillParameterMutation({
            onSuccess: () => {
                refetchSkills();
            }
        });

    const handleCreateParameter = useCallback(
        async (data: TSkillParameterData) => {
            if (!skillId) {
                throw new Error('Skill ID is required');
            }
            return createSkillParameterAsync({ libraryId, skillId, data });
        },
        [createSkillParameterAsync, libraryId, skillId]
    );

    const handleDeleteParameter = useCallback(
        async (parameter: TSkillParameter) => {
            if (!skillId) {
                throw new Error('Skill ID is required');
            }
            openConfirmDeleteModal({
                title: `Delete ${parameter.name} parameter?`,
                children: `Are you sure you want to delete ${parameter.name} parameter?`,
                confirmLabel: 'Delete parameter',
                onConfirm: () => {
                    deleteSkillParameterAsync(
                        {
                            libraryId,
                            skillId,
                            parameterId: parameter.id
                        },
                        {
                            onSuccess: () => {
                                refetchSkills();
                            }
                        }
                    );
                }
            });
        },
        [deleteSkillParameterAsync, libraryId, refetchSkills, skillId]
    );

    const handleUpdateParameter = useCallback(
        async (parameterId: string, data: TSkillParameterData) => {
            if (!skillId) {
                throw new Error('Skill ID is required');
            }
            return updateSkillParameterAsync({
                libraryId,
                skillId,
                parameterId,
                data
            });
        },
        [updateSkillParameterAsync, libraryId, skillId]
    );

    return {
        onParameterCreate: handleCreateParameter,
        onParameterDelete: handleDeleteParameter,
        onParameterUpdate: handleUpdateParameter
    };
};

export const useCodeEditorState = (skills: TSkill[] = []) => {
    const skillScripts = useLibraryStore((state) => state.skillScripts);
    const hasUnsavedScripts = useMemo(() => {
        return skills.some((skill) => {
            const script = skillScripts[skill.id];
            return typeof script === 'string' && skill.prompt_script !== script;
        });
    }, [skills, skillScripts]);
    return { hasUnsavedScripts };
};
